import React from 'react';

/**
 * Renders free-form note text with support for simple bullet lists.
 *
 * Lines that start with "- ", "* ", or "• " (optionally indented) are
 * grouped into <ul> blocks so the customer-facing estimate looks like a
 * real bulleted list instead of dashes-and-text. All other lines flow
 * through as paragraphs, preserving blank-line spacing.
 *
 * Kept intentionally tiny — no markdown lib, no HTML injection, just
 * text-safe rendering that upgrades bullets without breaking anything
 * else contractors type into the notes field.
 */
export default function FormattedNotes({ text, className = '' }) {
  const value = (text || '').trim();
  if (!value) return null;

  const lines = value.split('\n');
  const blocks = [];
  let currentList = null;
  let paragraphBuffer = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length === 0) return;
    blocks.push({ type: 'p', lines: paragraphBuffer });
    paragraphBuffer = [];
  };

  const flushList = () => {
    if (!currentList) return;
    blocks.push(currentList);
    currentList = null;
  };

  for (const raw of lines) {
    const bulletMatch = raw.match(/^\s*[-*•]\s+(.*)$/);
    if (bulletMatch) {
      flushParagraph();
      if (!currentList) currentList = { type: 'ul', items: [] };
      currentList.items.push(bulletMatch[1]);
    } else if (raw.trim() === '') {
      // Blank line → end current list, break paragraph
      flushList();
      flushParagraph();
    } else {
      flushList();
      paragraphBuffer.push(raw);
    }
  }
  flushList();
  flushParagraph();

  return (
    <div className={`space-y-2 ${className}`} data-testid="formatted-notes">
      {blocks.map((block, idx) => {
        if (block.type === 'ul') {
          return (
            <ul key={idx} className="list-disc pl-5 space-y-1">
              {block.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={idx} className="whitespace-pre-wrap leading-relaxed">
            {block.lines.join('\n')}
          </p>
        );
      })}
    </div>
  );
}
